import { useEffect, useState } from "react";
import api from "@/lib/api";
import FunnelBars from "@/components/FunnelBars";
import FiltersBar from "@/components/FiltersBar";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

function Stat({ label, value, accent = "#00F0FF" }) {
  return (
    <div className="card-tactical p-5">
      <div className="kbd-label">{label}</div>
      <div className="stat-value text-3xl mt-2" style={{ color: accent }}>{value}</div>
    </div>
  );
}

export default function OverviewTab() {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({});

  async function load() {
    const params = {};
    if (filters.campaign) params.campaign = filters.campaign;
    if (filters.from_date) params.from_date = filters.from_date;
    if (filters.to_date) params.to_date = filters.to_date + "T23:59:59";
    const r = await api.get("/analytics/overview", { params });
    setData(r.data);
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  if (!data) return <div className="kbd-label text-neon-cyan">// LOADING</div>;
  const t = data.totals;
  const chartData = data.creators.map((c) => ({
    name: c.slug,
    clicks: c.clicks,
    members: c.current_members,
  }));

  return (
    <div className="space-y-6">
      <FiltersBar value={filters} onChange={setFilters} />

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="TOTAL CLICKS" value={t.clicks} />
        <Stat label="CURRENT MEMBERS" value={t.current_members} accent="#E4FF00" />
        <Stat label="IN-WINDOW NET REVENUE" value={`$${t.net_revenue}`} accent="#22C55E" />
        <Stat label="OUT-OF-WINDOW REVENUE" value={`$${t.out_of_window_gross}`} accent="#FF3B30" />
      </section>

      <section className="grid md:grid-cols-5 gap-4">
        <div className="card-tactical p-6 md:col-span-3">
          <h2 className="display-heading font-bold text-lg mb-4">GLOBAL FUNNEL</h2>
          <FunnelBars data={t} />
        </div>
        <div className="card-tactical p-6 md:col-span-2">
          <h2 className="display-heading font-bold text-lg mb-4">CURRENT MEMBERS · BY CREATOR</h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} margin={{ left: -20 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fill: "#a1a1aa", fontFamily: "JetBrains Mono", fontSize: 10 }} />
              <YAxis tick={{ fill: "#a1a1aa", fontFamily: "JetBrains Mono", fontSize: 10 }} />
              <Tooltip contentStyle={{ background: "#0F0F0F", border: "1px solid rgba(255,255,255,0.1)", fontFamily: "JetBrains Mono" }} />
              <Bar dataKey="members" fill="#E4FF00" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="card-tactical">
        <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center">
          <h2 className="display-heading font-bold text-lg">CREATOR LEADERBOARD</h2>
          <span className="kbd-label text-white/40">{data.creators.length} CREATORS</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="kbd-label text-left text-white/50 border-b border-white/10">
                <th className="px-6 py-3">CREATOR</th>
                <th className="px-3">CLICKS</th>
                <th className="px-3">MEMBERS</th>
                <th className="px-3">LEFT</th>
                <th className="px-3">WINDOW</th>
                <th className="px-3">IN-WIN NET</th>
                <th className="px-3">OUT-OF-WIN</th>
                <th className="px-3">EARNINGS</th>
                <th className="px-3">CVR %</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {data.creators.map((c) => (
                <tr key={c.creator_id} className="border-b border-white/5 hover:bg-white/[0.03]">
                  <td className="px-6 py-3">
                    <div className="font-sans">{c.display_name}</div>
                    <div className="text-xs text-white/40">@{c.slug}</div>
                  </td>
                  <td className="px-3">{c.clicks}</td>
                  <td className="px-3 text-neon-yellow">{c.current_members}</td>
                  <td className="px-3 text-red-400">{c.left_members}</td>
                  <td className="px-3 text-neon-cyan">{(c.attribution_window_days ?? 30) === 0 ? "∞" : `${c.attribution_window_days ?? 30}d`}</td>
                  <td className="px-3">${c.net_revenue}</td>
                  <td className="px-3 text-red-400">${c.out_of_window_gross}</td>
                  <td className="px-3 text-emerald-400">${c.creator_earnings}</td>
                  <td className="px-3 text-neon-cyan">{c.conversion_rate}%</td>
                </tr>
              ))}
              {data.creators.length === 0 && <tr><td colSpan={9} className="px-6 py-6 text-white/40">No creators yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
